import {
  Alert,
  Button,
  Flex,
  Heading,
  IconWarningSolid,
  RadioInput,
  RadioInputGroup,
  Text,
  ToggleDetails,
  Tooltip,
  View,
} from "@instructure/ui";
import "c3/c3.css";
import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import Review from "../components/Outcomes/Review/Review";
import { UserDetailContext } from "../utils/Context";

import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { Card } from "../components/Card";
import {
  ERROR,
  useNotification,
} from "../components/CustomHooks/NotificationProvider";
import { TaxonomySelect } from "../components/LearningOutcomes/TaxonomySelect";
import Chart from "../components/Outcomes/Chart";
import { CourseFilters } from "../components/Outcomes/Review/CourseFilters";
import { CourseLevelService } from "../services/CourseLevelService";
import { QualityCheckService } from "../services/QualityCheckService";
import { SubjectService } from "../services/SubjectService";

export const RefreshMetricsContext = createContext();

const Outcomes = () => {
  const { t } = useTranslation();
  const userDetails = useContext(UserDetailContext);
  const showNotification = useNotification();

  const initialFilters = {
    subject: "",
    courseLevels: [],
    programs: [],
    courseGroups: [],
    courses: [],
    taxonomyLevels: [],
    outcomeFilters: { approval: "off", verbCount: "off", outcomeLength: "off" },
    LOType: userDetails.isAC() ? "ISLO" : "PSLO",
    variety: ["outcome", "standard"],
    categories: [],
    masteryResults: "true",
    terms: [],
  };

  const filtersReducer = (state, action) => {
    switch (action.type) {
      case "UPDATE_FILTER":
        return { ...state, [action.field]: action.value };
      case "UPDATE_OUTCOME_FILTER":
        return {
          ...state,
          outcomeFilters: {
            ...state.outcomeFilters,
            [action.field]: action.value,
          },
        };
      case "RESET_FILTER":
        return { ...initialFilters, subject: action.value };
      default:
        return state;
    }
  };

  const [filters, dispatch] = useReducer(filtersReducer, initialFilters);
  const [comparisonFilter, setComparisonFilter] = useState(initialFilters);
  const [chartTrigger, setChartTrigger] = useState(false);
  const [outcomesTrigger, setOutcomesTrigger] = useState(false);
  const [clear, setClear] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [defaultSubject, setDefaultSubject] = useState("");
  const [disableTaxFilter, setDisableTaxFilter] = useState(false);
  const [disableLengthFilter, setDisableLengthFilter] = useState(false);
  const [refreshMetrics, setRefreshMetrics] = useState(false);
  const [courseLevels, setCourseLevels] = useState([]);

  const [selectedCourses, setSelectedCourses] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);

  useEffect(() => {
    if (disableTaxFilter) {
      dispatch({
        type: "UPDATE_OUTCOME_FILTER",
        field: "verbCount",
        value: "off",
      });
    }
  }, [disableTaxFilter]);

  useEffect(() => {
    if (disableLengthFilter) {
      dispatch({
        type: "UPDATE_OUTCOME_FILTER",
        field: "outcomeLength",
        value: "off",
      });
    }
  }, [disableLengthFilter]);

  useEffect(() => {
    if (clear) setClear(false);
  }, [clear]);

  useEffect(() => {
    if (chartTrigger) setChartTrigger(false);
  }, [chartTrigger]);

  useEffect(() => {
    if (filters.LOType === "CSLO" && selectedCourses.length === 0) {
      dispatch({
        type: "UPDATE_FILTER",
        field: "LOType",
        value: initialFilters.LOType,
      });
    }
  }, [filters.LOType, filters.courses]);

  useEffect(() => {
    if (defaultSubject) {
      dispatch({
        type: "UPDATE_FILTER",
        field: "subject",
        value: defaultSubject,
      });
      setComparisonFilter((prevFilter) => ({
        ...prevFilter,
        subject: defaultSubject,
      }));
    }
  }, [defaultSubject]);

  useEffect(() => {
    const fetchSubjectInfo = async () => {
      if (userDetails.role) {
        try {
          const subjectPromise = SubjectService.getSubjects();
          const unapprovedCountPromise =
            QualityCheckService.getUnapprovedOutcomeCount();
          const courseLevelPromise = CourseLevelService.getCourseLevels();

          const [academicSubjects, countsBySubject, levels] = await Promise.all(
            [subjectPromise, unapprovedCountPromise, courseLevelPromise]
          );
          setCourseLevels(levels);

          const selectOptions = [];
          academicSubjects.forEach((s) => {
            if (
              userDetails.isAC() ||
              (userDetails.isPC() && userDetails.ac_subjects.includes(s.title))
            ) {
              if (!!s.name && s.name !== " ") {
                const subjectInfo = { ...s, label: s.name };
                const sub = countsBySubject.find((c) => c.id === s.title);
                if (!!sub && sub.unapproved_outcome_count > 0) {
                  subjectInfo.unapproved_outcome_count =
                    sub.unapproved_outcome_count;
                  subjectInfo.renderAfterLabel = (
                    <Text size="small" fontStyle="italic">{`${
                      sub.unapproved_outcome_count
                    } ${t("analyze.unapproved")}`}</Text>
                  );
                }
                selectOptions.push(subjectInfo);
              }
            }
          });
          setSubjects(selectOptions);
          if (userDetails.isAC()) {
            dispatch({ type: "UPDATE_FILTER", field: "LOType", value: "ISLO" });
            setDefaultSubject(selectOptions[0].title);
          } else {
            const firstSubject = userDetails.ac_subjects.sort()[0];
            dispatch({ type: "UPDATE_FILTER", field: "LOType", value: "PSLO" });
            setDefaultSubject(firstSubject);
          }
          setChartTrigger(true);
          setOutcomesTrigger(true);
        } catch {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("global.departments"),
            }),
            ERROR
          );
        }
      }
    };
    fetchSubjectInfo();
  }, [userDetails, showNotification]);

  const handleCharLimitChange = (e) => {
    dispatch({
      type: "UPDATE_OUTCOME_FILTER",
      field: "outcomeLength",
      value: e.target.value,
    });
  };

  const handleVerbCountChange = (e) => {
    dispatch({
      type: "UPDATE_OUTCOME_FILTER",
      field: "verbCount",
      value: e.target.value,
    });
  };

  const handleApprovalChange = (e) => {
    dispatch({
      type: "UPDATE_OUTCOME_FILTER",
      field: "approval",
      value: e.target.value,
    });
  };

  const handleClear = () => {
    setClear(true);
    dispatch({ type: "RESET_FILTER", value: defaultSubject });
    setSelectedCourses([]);
  };

  const listDifference = (list1, list2) => {
    return (
      list1.length !== list2.length || !list1.every((v) => list2.includes(v))
    );
  };

  const handleTrigger = () => {
    if (
      filters.subject !== comparisonFilter.subject ||
      listDifference(filters.courseLevels, comparisonFilter.courseLevels) ||
      listDifference(filters.programs, comparisonFilter.programs) ||
      listDifference(filters.courseGroups, comparisonFilter.courseGroups) ||
      listDifference(filters.courses, comparisonFilter.courses) ||
      listDifference(filters.taxonomyLevels, comparisonFilter.taxonomyLevels) ||
      filters.outcomeFilters.approval !==
        comparisonFilter.outcomeFilters.approval ||
      filters.outcomeFilters.verbCount !==
        comparisonFilter.outcomeFilters.verbCount ||
      filters.outcomeFilters.outcomeLength !==
        comparisonFilter.outcomeFilters.outcomeLength
    ) {
      setOutcomesTrigger(true);
    }
    if (
      filters.LOType !== comparisonFilter.LOType ||
      filters.subject !== comparisonFilter.subject ||
      listDifference(filters.terms, comparisonFilter.terms) ||
      listDifference(filters.courseLevels, comparisonFilter.courseLevels) ||
      filters.masteryResults !== comparisonFilter.masteryResults ||
      listDifference(filters.categories, comparisonFilter.categories) ||
      listDifference(filters.variety, comparisonFilter.variety) ||
      listDifference(filters.courses, comparisonFilter.courses)
    ) {
      setChartTrigger(true);
    }
    setComparisonFilter(filters);
  };

  const MainCourseFilters = () => {
    return (
      <>
        <Heading as="h2" level="h3" margin="0 0 small 0">
          {t("analyze.courseFilterHeading")}
        </Heading>
        {subjects.length > 0 ? (
          <CourseFilters
            filters={filters}
            dispatch={dispatch}
            subjects={subjects}
            clear={clear}
            selectedCourses={selectedCourses}
            setSelectedCourses={setSelectedCourses}
            courseLevels={courseLevels}
            setCourseOptionsParent={setCourseOptions}
          />
        ) : (
          <Flex justifyItems="space-between" alignItems="start" wrap="wrap">
            <Flex.Item size="33%" margin="0 0 small 0">
              <Skeleton height={58} />
            </Flex.Item>
            {Array.from(Array(5).keys()).map((i) => (
              <Flex.Item key={i} size="33%">
                <Skeleton height={58} />
              </Flex.Item>
            ))}
          </Flex>
        )}
      </>
    );
  };

  const AdditionalOutcomeFilters = () => {
    return (
      <ToggleDetails
        fluidWidth
        summary={
          <Flex gap="small">
            <Flex.Item>
              <Heading as="h2" level="h4">
                {t("analyze.additionalOutcomeFilters")}
              </Heading>
            </Flex.Item>
            <Flex.Item>
              {(disableLengthFilter || disableTaxFilter) && (
                <Tooltip
                  color="primary-inverse"
                  renderTip={t(
                    "notifications.analyze.configureOutcomeQualityFilter"
                  )}
                  placement="end"
                >
                  <IconWarningSolid color="warning" />
                </Tooltip>
              )}
            </Flex.Item>
          </Flex>
        }
      >
        <Flex justifyItems="space-between" wrap="wrap">
          <TaxonomySelect
            size="small"
            taxonomyId="blooms"
            selectHandler={(taxonomyLevels) =>
              dispatch({
                type: "UPDATE_FILTER",
                field: "taxonomyLevels",
                value: taxonomyLevels,
              })
            }
            clear={clear}
            needLabel={true}
          />
          <Flex.Item>
            <RadioInputGroup
              size="small"
              variant="toggle"
              onChange={handleCharLimitChange}
              name="charLimitFilter"
              disabled={disableLengthFilter}
              value={filters.outcomeFilters.outcomeLength}
              description={
                <Text size="small">
                  {t("analyze.charLimitFilterRadios.label")}
                </Text>
              }
            >
              <RadioInput value="off" label={t("analyze.off")} context="off" />
              <RadioInput
                value="notMeet"
                label={t("analyze.doesNotMeet")}
                context="danger"
              />
              <RadioInput value="meets" label={t("analyze.meets")} />
            </RadioInputGroup>
          </Flex.Item>
          <Flex.Item>
            <RadioInputGroup
              size="small"
              variant="toggle"
              onChange={handleVerbCountChange}
              name="taxonomyFiltering"
              value={filters.outcomeFilters.verbCount}
              disabled={disableTaxFilter}
              description={
                <Text size="small">
                  {t("analyze.taxonomyFilteringRadios.label")}
                </Text>
              }
            >
              <RadioInput value="off" label={t("analyze.off")} context="off" />
              <RadioInput
                value="notMeet"
                label={t("analyze.doesNotMeet")}
                context="danger"
              />
              <RadioInput value="meets" label={t("analyze.meets")} />
            </RadioInputGroup>
          </Flex.Item>
          <Flex.Item>
            <RadioInputGroup
              size="small"
              variant="toggle"
              onChange={handleApprovalChange}
              name="statusFiltering"
              value={filters.outcomeFilters.approval}
              description={
                <Text size="small">
                  {t("analyze.statusFilterRadios.label")}
                </Text>
              }
            >
              <RadioInput value="off" label={t("analyze.off")} context="off" />
              <RadioInput
                value="unapproved"
                label={t("analyze.statusFilterRadios.unapproved")}
                context="danger"
              />
              <RadioInput
                value="approved"
                label={t("analyze.statusFilterRadios.approved")}
              />
            </RadioInputGroup>
          </Flex.Item>
        </Flex>
      </ToggleDetails>
    );
  };

  return (
    <div style={{ minWidth: 700 }}>
      <Heading as="h1" level="h2" border="bottom">
        {t("review.coursesAndOutcomes")}
      </Heading>
      {userDetails.isPC() && !userDetails.ac_subjects.length ? (
        <Alert variant="info">{t("dashboard.noAssociatedSubjects")}</Alert>
      ) : (
        <RefreshMetricsContext.Provider
          value={{ refreshMetrics, setRefreshMetrics, setSubjects }}
        >
          <Card
            style={{
              marginTop: 15,
              boxShadow: "none",
              backgroundColor: "#F2F3F5",
            }}
          >
            {MainCourseFilters()}
            <View as="div" margin="small 0 medium 0">
              {AdditionalOutcomeFilters()}
            </View>
            <Chart
              filters={filters}
              dispatch={dispatch}
              trigger={chartTrigger}
              setTrigger={setChartTrigger}
              clear={clear}
              subjects={subjects}
              selectedCourses={selectedCourses}
              courseOptions={courseOptions}
            />
            <Flex direction="row-reverse" margin="medium 0 0 0" gap="small">
              <Flex.Item>
                <Button
                  size="small"
                  id="apply-button"
                  color="primary"
                  onClick={handleTrigger}
                  interaction={!filters.subject ? "disabled" : "enabled"}
                >
                  {t("filter.applyButton")}
                </Button>
              </Flex.Item>
              <Flex.Item>
                <Button size="small" id="clear-button" onClick={handleClear}>
                  {t("analyze.clear")}
                </Button>
              </Flex.Item>
            </Flex>
          </Card>
          <Review
            filters={comparisonFilter}
            trigger={outcomesTrigger}
            setTrigger={setOutcomesTrigger}
            subLoad={subjects.length > 0}
            setDisableLengthFilter={setDisableLengthFilter}
            setDisableTaxFilter={setDisableTaxFilter}
            departments={subjects}
            courseLevels={courseLevels}
          />
        </RefreshMetricsContext.Provider>
      )}
    </div>
  );
};

export default Outcomes;
